/**
 * CrownIcon — the brand crown mark (web).
 *
 * Sourced from the custom SVG at `reignara_meridian/icons/crown-minimalistic.svg`
 * — a solid, filled minimalist crown (single fill path, no outline).
 * Inlined here (rather than loaded as an asset) so it scales cleanly and
 * inherits `currentColor`.
 *
 * API matches the Phosphor icon shape (`size` / `color` / `weight`) so it
 * slots into the icon system. NOTE: this is a filled silhouette, so
 * `weight` is accepted for compatibility but does not change rendering
 * (there is no separate outline/duotone variant for this glyph).
 */
import type { SVGProps } from 'react';

export type CrownWeight = 'regular' | 'duotone';

export interface CrownIconProps extends Omit<SVGProps<SVGSVGElement>, 'color'> {
  /** Pixel size for both width and height. */
  size?: number;
  /** Fill color. Defaults to `currentColor`. */
  color?: string;
  /** Accepted for API compatibility; this filled glyph renders the same regardless. */
  weight?: CrownWeight;
}

const CROWN_PATH =
  'M21.609 13.5616L21.8382 11.1263C22.0182 9.2137 22.1082 8.25739 21.781 7.86207C21.604 7.64823 21.3633 7.5172 21.106 7.4946C20.6303 7.45282 20.0329 8.1329 18.8381 9.49307C18.2202 10.1965 17.9113 10.5482 17.5666 10.6027C17.3757 10.6328 17.1811 10.6018 17.0047 10.5131C16.6865 10.3529 16.4743 9.91812 16.0499 9.04851L13.8131 4.46485C13.0112 2.82162 12.6102 2 12 2C11.3898 2 10.9888 2.82162 10.1869 4.46486L7.95007 9.04852C7.5257 9.91812 7.31351 10.3529 6.99526 10.5131C6.81892 10.6018 6.62434 10.6328 6.43337 10.6027C6.08872 10.5482 5.77977 10.1965 5.16187 9.49307C3.96708 8.1329 3.36968 7.45282 2.89399 7.4946C2.63666 7.5172 2.39598 7.64823 2.21899 7.86207C1.8918 8.25739 1.9818 9.2137 2.16181 11.1263L2.391 13.5616C2.76865 17.5742 2.95748 19.5805 4.14009 20.7902C5.32271 22 7.09517 22 10.6401 22H13.3599C16.9048 22 18.6773 22 19.8599 20.7902C21.0425 19.5805 21.2313 17.5742 21.609 13.5616Z';

export function CrownIcon({
  size = 24,
  color = 'currentColor',
  weight: _weight,
  ...rest
}: CrownIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      {...rest}
    >
      <path d={CROWN_PATH} fill={color} />
    </svg>
  );
}

export default CrownIcon;
